import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { messagesKey } from '../constants';
import {
  structuredDataToBlock,
  toCandidateProfilesBlock,
  wantsFullProfiles,
} from '../helpers';
import agentforgeHttp from '@/lib/agentforge-http';
import type { ServerMessageInput } from '../stores/agentForgeStore';
import type { BlockOutput } from '../types';
import type { ActionTrailStep, MessageAttachment } from '../types/transcript';
import { callAgentForge } from './http';
import type {
  ExecuteActionInput,
  ExecuteActionResponse,
  ListMessagesArgs,
  MessagesResponse,
  SendMessageInput,
  SendMessageResponse,
  ServerMessage,
  UseMessagesArgs,
} from './messages.types';

const MESSAGES_STALE_TIME_MS = 1_000;

// Hide internal turns that shouldn't render as their own bubble. Legacy
// `/agentforge` does the same: tool outputs and empty intermediate
// assistant turns are noise — the final assistant prose already
// summarises whatever happened, so showing them double-renders.
// Pull uploaded-file attachments out of a user message's metadata JSONB. The
// backend persists `{ attachments: [{ id, filename, content_type, size }] }`;
// the bytes live in the FileStore (S3), reachable via the download route.
const parseAttachments = (
  metadata: Record<string, unknown> | undefined
): MessageAttachment[] | undefined => {
  const raw = metadata?.attachments;
  if (!Array.isArray(raw)) return undefined;
  const atts = raw.flatMap((a) => {
    if (!a || typeof a !== 'object') return [];
    const o = a as Record<string, unknown>;
    if (typeof o.id !== 'string' || typeof o.filename !== 'string') return [];
    return [
      {
        id: o.id,
        filename: o.filename,
        contentType:
          typeof o.content_type === 'string' ? o.content_type : undefined,
        size: typeof o.size === 'number' ? o.size : undefined,
      },
    ];
  });
  return atts.length > 0 ? atts : undefined;
};

// Download an uploaded attachment. The endpoint requires the bearer token, so
// we fetch the bytes through the authenticated client (a plain <a href> would
// 401) and trigger a browser download from an in-memory object URL.
export const downloadAttachment = async (
  sessionId: string,
  attachment: MessageAttachment
): Promise<void> => {
  const blob = (await agentforgeHttp.get(
    `/sessions/${sessionId}/attachments/${attachment.id}/${encodeURIComponent(
      attachment.filename
    )}`,
    { responseType: 'blob' }
  )) as unknown as Blob;
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = attachment.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

const isVisibleMessage = (m: ServerMessage): boolean => {
  if (m.role === 'tool' || m.role === 'system') return false;
  if (m.role === 'assistant' && !m.content?.trim()) return false;
  return true;
};

// Extract a renderable BlockOutput from a tool message. Tool messages carry
// the rich payload on `metadata.structured_data`; we run it through the
// same mapper the WS path uses so refreshed sessions look identical to
// live-streamed ones.
const blockFromToolMessage = (m: ServerMessage): BlockOutput | null => {
  if (m.role !== 'tool') return null;
  const meta = m.metadata as
    | { structured_data?: unknown; tool_call_id?: unknown }
    | undefined;
  if (!meta?.structured_data) return null;
  const toolCallId =
    typeof meta.tool_call_id === 'string' ? meta.tool_call_id : undefined;
  return structuredDataToBlock(meta.structured_data, toolCallId);
};

// trailStepFromToolMessage rebuilds one "what the agent did" row from a
// persisted tool message (metadata.tool_name). Durations aren't persisted —
// the row still humanises via the shared tool-label mapping.
const trailStepFromToolMessage = (m: ServerMessage): ActionTrailStep | null => {
  if (m.role !== 'tool') return null;
  const meta = m.metadata as
    | { tool_name?: unknown; tool_call_id?: unknown }
    | undefined;
  const toolName = typeof meta?.tool_name === 'string' ? meta.tool_name : '';
  // Skip interaction cards (clarify forms/plans) — "Waiting for your input"
  // reads wrong on a finished turn; real work rows only. `remember` is the
  // agent's private note-taking — showing "Remember" as a completed step
  // exposes internal bookkeeping the user can't act on.
  if (!toolName || toolName.startsWith('propose_') || toolName === 'remember')
    return null;
  const id = typeof meta?.tool_call_id === 'string' ? meta.tool_call_id : m.id;
  return { id, name: toolName, status: 'done' };
};

// Walk the transcript in order. Accumulate any blocks from tool messages
// since the last visible turn, then attach them to the next assistant
// turn so its `outputs` includes both the prose summary AND the rich
// blocks the tool produced. Mirrors what commitStreamingDraft does on
// the live WS path. The same fold rebuilds the completed-step trail so the
// "what the agent did" footer survives hydration instead of disappearing.
export const toStoreInputs = (
  messages: ServerMessage[]
): ServerMessageInput[] => {
  const out: ServerMessageInput[] = [];
  let pendingBlocks: BlockOutput[] = [];
  let pendingTrail: ActionTrailStep[] = [];
  // INTERIM: last user message drives the full-profile carousel promotion.
  let lastUserText = '';
  for (const m of messages) {
    if (m.role === 'tool') {
      let block = blockFromToolMessage(m);
      if (block?.type === 'candidate' && wantsFullProfiles(lastUserText)) {
        block = toCandidateProfilesBlock(block);
      }
      if (block) pendingBlocks.push(block);
      const step = trailStepFromToolMessage(m);
      if (step) pendingTrail.push(step);
      continue;
    }
    if (!isVisibleMessage(m)) {
      // e.g. an empty assistant turn that was just a tool-call decision.
      // Pending blocks survive — they belong to the *final* assistant
      // turn that summarises the tool output, not to this empty one.
      continue;
    }
    if (m.role === 'user') {
      // Pending blocks here mean the previous turn ended without a prose
      // anchor (clarify-only turn) or a message interleaved mid-turn.
      // Flush result cards into a synthesized assistant turn so they
      // survive the fold; drop consumed pickers (the user's submit bubble
      // is their record — re-rendering the form would invite a stale
      // second submit).
      const keep = pendingBlocks.filter(
        (b) => b.type !== 'clarify' && b.type !== 'clarifyForm'
      );
      if (keep.length > 0) {
        out.push({
          id: `assistant-preflush-${m.id}`,
          role: 'assistant',
          content: '',
          createdAt: m.created_at,
          outputs: keep,
          actionTrail: pendingTrail.length > 0 ? pendingTrail : undefined,
        });
      }
      pendingBlocks = [];
      pendingTrail = [];
      lastUserText = m.content;
      out.push({
        id: m.id,
        role: 'user',
        content: m.content,
        createdAt: m.created_at,
        attachments: parseAttachments(m.metadata),
      });
      continue;
    }
    // Assistant turn with text. Pair it with any blocks the tools
    // produced since the previous visible turn.
    const outputs: BlockOutput[] = [];
    if (m.content) {
      outputs.push({ type: 'text', data: { content: m.content } });
    }
    outputs.push(...pendingBlocks);
    pendingBlocks = [];
    out.push({
      id: m.id,
      role: 'assistant',
      content: m.content,
      createdAt: m.created_at,
      outputs,
      actionTrail: pendingTrail.length > 0 ? pendingTrail : undefined,
    });
    pendingTrail = [];
  }
  // Orphan blocks at end-of-transcript: typical when the agent finished a
  // turn by emitting a card-only tool (propose_clarify) and stopping with
  // no prose to anchor them to. Synthesize a trailing assistant turn that
  // carries them so hydrate doesn't silently drop the picker/results.
  if (pendingBlocks.length > 0) {
    const lastTool = [...messages].reverse().find((m) => m.role === 'tool');
    const anchorId = lastTool?.id ?? `assistant-orphan-${Date.now()}`;
    const createdAt = lastTool?.created_at ?? new Date().toISOString();
    out.push({
      id: `assistant-${anchorId}`,
      role: 'assistant',
      content: '',
      createdAt,
      outputs: pendingBlocks,
      actionTrail: pendingTrail.length > 0 ? pendingTrail : undefined,
    });
  }
  return out;
};

// Polling client — no normalization. The BE returns flat `content` strings;
// the adapter from server shape → store TranscriptMessage lives in the
// agentforge store (`hydrateMessages`).
const listMessages = async ({
  sessionId,
  limit = 100,
  offset = 0,
  signal,
}: ListMessagesArgs): Promise<ServerMessage[]> => {
  const body = await callAgentForge<MessagesResponse>({
    url: `/sessions/${sessionId}/messages`,
    params: { limit, offset },
    signal,
  });
  return body.messages ?? [];
};

const sendMessage = (input: SendMessageInput): Promise<SendMessageResponse> => {
  // With attachments, send multipart/form-data so the backend receives the
  // file bytes (axios sets the multipart Content-Type + boundary automatically
  // when the request body is a FormData). Otherwise keep the plain JSON body.
  if (input.files && input.files.length > 0) {
    const form = new FormData();
    form.append('message', input.message);
    if (input.timezone) form.append('timezone', input.timezone);
    input.files.forEach((file) => form.append('files', file, file.name));
    return callAgentForge<SendMessageResponse>({
      method: 'POST',
      url: `/sessions/${input.sessionId}/messages`,
      body: form,
    });
  }
  return callAgentForge<SendMessageResponse>({
    method: 'POST',
    url: `/sessions/${input.sessionId}/messages`,
    body: { message: input.message, timezone: input.timezone },
  });
};

const executeAction = (
  input: ExecuteActionInput
): Promise<ExecuteActionResponse> =>
  callAgentForge<ExecuteActionResponse>({
    method: 'POST',
    url: `/sessions/${input.sessionId}/actions`,
    body: { action: input.action, params: input.params },
  });

export const useMessagesQuery = (
  args: UseMessagesArgs
): UseQueryResult<ServerMessage[], Error> => {
  const sessionId = args.sessionId ?? '';
  const canRun = (args.enabled ?? true) && sessionId.length > 0;
  return useQuery({
    queryKey: [
      ...messagesKey(sessionId),
      { limit: args.limit, offset: args.offset },
    ],
    queryFn: ({ signal }) =>
      listMessages({
        sessionId,
        limit: args.limit,
        offset: args.offset,
        signal,
      }),
    enabled: canRun,
    staleTime: MESSAGES_STALE_TIME_MS,
    refetchInterval: args.refetchIntervalMs ?? false,
  });
};

export const useSendMessageMutation = (): UseMutationResult<
  SendMessageResponse,
  Error,
  SendMessageInput
> => useMutation({ mutationFn: sendMessage });

export const useExecuteActionMutation = (): UseMutationResult<
  ExecuteActionResponse,
  Error,
  ExecuteActionInput
> => useMutation({ mutationFn: executeAction });
